import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Room, RoomEvent, type RemoteTrack } from "livekit-client";
import ChatCallService from "@/services/ChatCallService";
import { getSocket } from "@/services/socketService";
import type {
  ChatCall,
  ChatCallConfirmationResult,
  ChatCallParticipant,
} from "@/types/chatCall";
import { getStoredAccessToken } from "@/utils/authStorage";
import { toast } from "react-toastify";

const CALL_LOCK_KEY = "goon:chat-call:lock";
const LOCK_STALE_MS = 15000;
const REMOTE_ABSENCE_GRACE_MS = 15000;

type RoomCandidate = {
  id?: string | number;
  is_group?: boolean;
  users?: Array<{
    id?: number;
    name?: string;
    last_name?: string;
    email?: string;
    url_img?: { path?: string } | string | null;
  }>;
};

type ConnectionState = "idle" | "connecting" | "connected" | "reconnecting";

type PendingCallRequest = {
  roomId: string;
  participant: ChatCallParticipant;
};

type CallLock = {
  callId: string;
  ownerTabId: string;
  updatedAt: number;
};

type ErrorWithMessage = {
  response?: {
    data?: {
      code?: string;
      message?: string;
    };
  };
  message?: string;
  name?: string;
};

interface UseChatCallManagerParams {
  enabled: boolean;
  userId?: number;
}

interface UseChatCallManagerResult {
  activeCall: ChatCall | null;
  isCallActionLoading: boolean;
  isCallMuted: boolean;
  isIncomingCallOpen: boolean;
  isOngoingCallOpen: boolean;
  isCallControlOwner: boolean;
  ringtoneBlocked: boolean;
  callDurationSeconds: number;
  callConnectionState: ConnectionState;
  isCallConfirmationOpen: boolean;
  callConfirmationParticipant: ChatCallParticipant | null;
  callConfirmationResult: ChatCallConfirmationResult | null;
  requestVoiceCall: (room: RoomCandidate) => Promise<void>;
  confirmVoiceCall: () => Promise<void>;
  cancelVoiceCallConfirmation: () => void;
  retryVoiceCallConfirmation: () => void;
  closeCallForRoom: (roomId: string | number, reason?: string) => Promise<void>;
  answerIncomingCall: () => Promise<void>;
  rejectIncomingCall: () => Promise<void>;
  cancelOutgoingCall: () => Promise<void>;
  endCurrentCall: () => Promise<void>;
  toggleCallMicrophone: () => Promise<void>;
}

function getAvatarUrl(
  avatar?: { path?: string } | string | null,
): string | null | undefined {
  if (typeof avatar === "string") return avatar;
  return avatar?.path || null;
}

function readCallLock(): CallLock | null {
  try {
    const value = localStorage.getItem(CALL_LOCK_KEY);
    return value ? (JSON.parse(value) as CallLock) : null;
  } catch {
    return null;
  }
}

function isTerminalCallStatus(call?: ChatCall | null) {
  const status = call?.rawStatus || call?.status;
  return [
    "declined",
    "cancelled",
    "missed",
    "ended",
    "connection_failed",
    "microphone_denied",
    "recipient_unavailable",
  ].includes(status || "");
}

function isLiveCallStatus(call?: ChatCall | null) {
  const status = call?.rawStatus || call?.status;
  return ["calling", "answered", "in_progress"].includes(status || "");
}

function isConnectedCallStatus(call?: ChatCall | null) {
  const status = call?.rawStatus || call?.status;
  return ["answered", "in_progress"].includes(status || "");
}

function resolveTerminationAction(call?: ChatCall | null) {
  if (!call) return null;

  const status = call.rawStatus || call.status;
  if (status === "calling") {
    if (call.canCancel || call.isCaller) {
      return "cancel";
    }

    if (call.canAnswer || !call.isCaller) {
      return "reject";
    }
  }

  if (["answered", "in_progress"].includes(status || "")) {
    return "end";
  }

  return null;
}

function resolveCallErrorMessage(error: unknown, fallback: string) {
  const typedError = error as ErrorWithMessage | null;
  return typedError?.response?.data?.message || typedError?.message || fallback;
}

function isMicrophonePermissionError(error: unknown) {
  const typedError = error as ErrorWithMessage | null;
  return (
    typedError?.name === "NotAllowedError" ||
    typedError?.name === "PermissionDeniedError"
  );
}

function buildParticipant(room: RoomCandidate, userId?: number) {
  const roomId = room?.id ? String(room.id) : "";
  const users = Array.isArray(room?.users) ? room.users : [];

  if (!roomId) {
    return {
      error: "Conversa inválida para iniciar a chamada.",
      roomId: "",
      participant: null,
    };
  }

  if (room?.is_group) {
    return {
      error: "Chamadas de voz estão disponíveis apenas para conversas individuais.",
      roomId,
      participant: null,
    };
  }

  const participant = users.find((item) => Number(item?.id) !== Number(userId));

  if (!participant?.id) {
    return {
      error:
        users.length === 0
          ? "Não foi possível identificar os participantes desta conversa. Reabra a conversa e tente novamente."
          : "A conversa não possui um destinatário válido para ligação.",
      roomId,
      participant: null,
    };
  }

  if (Number(participant.id) === Number(userId)) {
    return {
      error: "Não é possível ligar para você mesmo.",
      roomId,
      participant: null,
    };
  }

  return {
    error: null,
    roomId,
    participant: {
      id: Number(participant.id),
      name: participant.name || "",
      lastName: participant.last_name || "",
      email: participant.email || "",
      avatarUrl: getAvatarUrl(participant.url_img) || null,
    } satisfies ChatCallParticipant,
  };
}

export function useChatCallManager({
  enabled,
  userId,
}: UseChatCallManagerParams): UseChatCallManagerResult {
  const tabIdRef = useRef(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const liveKitRoomRef = useRef<Room | null>(null);
  const remoteAudioElementsRef = useRef<Map<string, HTMLMediaElement>>(
    new Map(),
  );
  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const activeCallRef = useRef<ChatCall | null>(null);
  const connectingCallIdRef = useRef<string | null>(null);
  const connectedCallIdRef = useRef<string | null>(null);
  const remoteAbsentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastTerminalSignatureRef = useRef<string | null>(null);
  const unloadTerminatedCallIdRef = useRef<string | null>(null);
  const confirmCallInFlightRef = useRef(false);
  const endCurrentCallRef = useRef<(reason?: string) => Promise<void>>(
    async () => undefined,
  );

  const [activeCall, setActiveCall] = useState<ChatCall | null>(null);
  const [pendingCallRequest, setPendingCallRequest] =
    useState<PendingCallRequest | null>(null);
  const [callConfirmationResult, setCallConfirmationResult] =
    useState<ChatCallConfirmationResult | null>(null);
  const [isCallActionLoading, setIsCallActionLoading] = useState(false);
  const [isCallMuted, setIsCallMuted] = useState(false);
  const [ringtoneBlocked, setRingtoneBlocked] = useState(false);
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);
  const [callConnectionState, setCallConnectionState] =
    useState<ConnectionState>("idle");
  const [isCallControlOwner, setIsCallControlOwner] = useState(true);

  const releaseCallLock = useCallback((callId?: string) => {
    const currentLock = readCallLock();

    if (
      currentLock &&
      currentLock.ownerTabId === tabIdRef.current &&
      (!callId || currentLock.callId === callId)
    ) {
      localStorage.removeItem(CALL_LOCK_KEY);
    }

    setIsCallControlOwner(true);
  }, []);

  const acquireCallLock = useCallback((callId: string) => {
    const currentLock = readCallLock();
    const now = Date.now();

    if (
      currentLock &&
      currentLock.callId === callId &&
      currentLock.ownerTabId === tabIdRef.current
    ) {
      localStorage.setItem(
        CALL_LOCK_KEY,
        JSON.stringify({
          ...currentLock,
          updatedAt: now,
        } satisfies CallLock),
      );
      setIsCallControlOwner(true);
      return true;
    }

    if (
      !currentLock ||
      currentLock.callId !== callId ||
      now - currentLock.updatedAt > LOCK_STALE_MS
    ) {
      localStorage.setItem(
        CALL_LOCK_KEY,
        JSON.stringify({
          callId,
          ownerTabId: tabIdRef.current,
          updatedAt: now,
        } satisfies CallLock),
      );
      setIsCallControlOwner(true);
      return true;
    }

    const isOwner = currentLock.ownerTabId === tabIdRef.current;
    setIsCallControlOwner(isOwner);
    return isOwner;
  }, []);

  const clearRemoteAbsentTimer = useCallback(() => {
    if (remoteAbsentTimerRef.current) {
      clearTimeout(remoteAbsentTimerRef.current);
      remoteAbsentTimerRef.current = null;
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  const startRingtone = useCallback(async () => {
    try {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio("/toque-ligacao.mp3");
        ringtoneRef.current.loop = true;
        ringtoneRef.current.volume = 0.7;
      }

      ringtoneRef.current.currentTime = 0;
      await ringtoneRef.current.play();
      setRingtoneBlocked(false);
    } catch {
      setRingtoneBlocked(true);
    }
  }, []);

  const cleanupRemoteAudioElements = useCallback(() => {
    remoteAudioElementsRef.current.forEach((element) => {
      try {
        element.pause();
      } catch {
        // noop
      }

      element.remove();
    });

    remoteAudioElementsRef.current.clear();
  }, []);

  const cleanupLiveKitRoom = useCallback(
    async (disconnectRoom = true, connectingCallId: string | null = null) => {
      clearRemoteAbsentTimer();
      connectedCallIdRef.current = null;
      setCallConnectionState("idle");
      setIsCallMuted(false);

      if (liveKitRoomRef.current) {
        const room = liveKitRoomRef.current;
        liveKitRoomRef.current = null;

        if (disconnectRoom) {
          try {
            room.disconnect();
          } catch {
            // noop
          }
        }
      }

      cleanupRemoteAudioElements();
      connectingCallIdRef.current = connectingCallId;
    },
    [cleanupRemoteAudioElements, clearRemoteAbsentTimer],
  );

  const requestMicrophonePermission = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Navegador sem suporte para acesso ao microfone.");
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
      },
    });

    stream.getTracks().forEach((track) => track.stop());
  }, []);

  const showTerminalToast = useCallback((call: ChatCall) => {
    const signature = `${call.id}:${call.rawStatus}:${call.endedAt || ""}:${call.endReason || ""}`;
    if (lastTerminalSignatureRef.current === signature) {
      return;
    }

    lastTerminalSignatureRef.current = signature;

    switch (call.rawStatus) {
      case "declined":
        toast.info("Chamada recusada.");
        break;
      case "cancelled":
        toast.info(
          call.isCaller
            ? "Chamada cancelada antes do atendimento."
            : "O autor cancelou a chamada.",
        );
        break;
      case "missed":
        toast.info("Chamada não atendida.");
        break;
      case "ended":
        toast.info("Chamada encerrada.");
        break;
      case "connection_failed":
        toast.error("Falha de conexão na chamada de voz.");
        break;
      case "microphone_denied":
        toast.error("O microfone não foi liberado para a chamada.");
        break;
      case "recipient_unavailable":
        break;
      default:
        break;
    }
  }, []);

  const showRecipientUnavailable = useCallback(
    (request: PendingCallRequest) => {
      setPendingCallRequest(request);
      setCallConfirmationResult({
        type: "recipient_unavailable",
        title: "Usuário indisponível",
        message:
          "Essa pessoa não está com a plataforma aberta ou não pode receber chamadas agora.",
      });
    },
    [],
  );

  const finalizeCallState = useCallback(
    async (
      call?: ChatCall | null,
      options?: {
        clearPendingRequest?: boolean;
      },
    ) => {
      stopHeartbeat();
      stopRingtone();
      await cleanupLiveKitRoom();
      releaseCallLock(call?.id);
      if (options?.clearPendingRequest !== false) {
        setPendingCallRequest(null);
      }
      setCallDurationSeconds(0);

      if (call) {
        showTerminalToast(call);

        if (
          (call.rawStatus || call.status) === "recipient_unavailable" &&
          call.isCaller
        ) {
          showRecipientUnavailable({
            roomId: String(call.roomId),
            participant: call.remoteParticipant,
          });
        }
      }

      setActiveCall(null);
    },
    [
      cleanupLiveKitRoom,
      releaseCallLock,
      showRecipientUnavailable,
      showTerminalToast,
      stopHeartbeat,
      stopRingtone,
    ],
  );

  const applyCallState = useCallback(
    async (call: ChatCall | null) => {
      activeCallRef.current = call;

      if (!call) {
        await finalizeCallState(null, {
          clearPendingRequest: false,
        });
        return;
      }

      if (isTerminalCallStatus(call)) {
        await finalizeCallState(call, {
          clearPendingRequest: true,
        });
        return;
      }

      setPendingCallRequest(null);
      setCallConfirmationResult(null);
      setActiveCall(call);
    },
    [finalizeCallState],
  );

  const syncCurrentCallFromServer = useCallback(async () => {
    if (!enabled || !userId) return;

    const response = await ChatCallService.getCurrentActive();
    if (response?.data?.success) {
      await applyCallState(response.data.data);
    }
  }, [applyCallState, enabled, userId]);

  const reportCallFailure = useCallback(
    async (
      call: ChatCall,
      code:
        | "connection_failed"
        | "microphone_denied"
        | "token_expired"
        | "recipient_unavailable",
      reason: string,
      details?: Record<string, unknown>,
    ) => {
      try {
        const response = await ChatCallService.failure(call.id, {
          code,
          reason,
          details: details || null,
        });

        if (response?.data?.success) {
          await applyCallState(response.data.data);
          return;
        }
      } catch {
        // noop
      }

      toast.error(reason);
      await finalizeCallState(null);
    },
    [applyCallState, finalizeCallState],
  );

  const terminateCall = useCallback(
    async (call: ChatCall, reason: string) => {
      const action = resolveTerminationAction(call);
      if (!action) {
        return false;
      }

      const response =
        action === "cancel"
          ? await ChatCallService.cancel(call.id, { reason })
          : action === "reject"
            ? await ChatCallService.reject(call.id, { reason })
            : await ChatCallService.end(call.id, { reason });

      if (response?.data?.success) {
        await applyCallState(response.data.data);
        return true;
      }

      if (response?.status === 404 || response?.status === 409) {
        return false;
      }

      throw new Error(
        resolveCallErrorMessage(response, "Não foi possível finalizar a chamada anterior."),
      );
    },
    [applyCallState],
  );

  const sendKeepaliveTermination = useCallback((call: ChatCall, reason: string) => {
    const action = resolveTerminationAction(call);
    const token = getStoredAccessToken();
    const baseUrl = (import.meta.env.VITE_REACT_API_URL as string | undefined)?.replace(
      /\/$/,
      "",
    );

    if (!action || !token || !baseUrl) {
      return;
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const empresaId = window.localStorage.getItem("empresaId");
    if (empresaId) {
      headers["x-empresa-id"] = empresaId;
    }

    try {
      void fetch(`${baseUrl}/chat/calls/${call.id}/${action}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reason }),
        keepalive: true,
      });
    } catch {
      // noop
    }
  }, []);

  const ensureRoomIsReadyForNewCall = useCallback(
    async (roomId: string) => {
      const currentResponse = await ChatCallService.getCurrentActive();
      const currentCall =
        currentResponse?.data?.success && currentResponse.data.data
          ? currentResponse.data.data
          : null;

      if (!currentCall) {
        if (activeCallRef.current && isLiveCallStatus(activeCallRef.current)) {
          await applyCallState(null);
        }
        return true;
      }

      if (!isLiveCallStatus(currentCall)) {
        await applyCallState(null);
        return true;
      }

      if (String(currentCall.roomId) !== roomId) {
        await applyCallState(currentCall);
        toast.warning("Finalize a chamada atual antes de iniciar outra.");
        return false;
      }

      try {
        await terminateCall(currentCall, "replaced_by_new_call");
      } catch (error: unknown) {
        toast.error(
          resolveCallErrorMessage(error, "Não foi possível limpar a chamada anterior."),
        );
        return false;
      }

      const followUpResponse = await ChatCallService.getCurrentActive();
      const followUpCall =
        followUpResponse?.data?.success && followUpResponse.data.data
          ? followUpResponse.data.data
          : null;

      if (followUpCall && isLiveCallStatus(followUpCall)) {
        await applyCallState(followUpCall);
        toast.warning("Ainda existe uma chamada ativa para esta conversa.");
        return false;
      }

      await applyCallState(null);
      return true;
    },
    [applyCallState, terminateCall],
  );

  const connectToLiveKit = useCallback(
    async (call: ChatCall) => {
      if (!call.livekit || !enabled || !userId) return;
      if (!isConnectedCallStatus(call)) return;
      if (!acquireCallLock(call.id)) return;
      if (connectingCallIdRef.current === call.id) return;
      if (connectedCallIdRef.current === call.id && liveKitRoomRef.current) {
        return;
      }

      connectingCallIdRef.current = call.id;
      stopRingtone();
      await cleanupLiveKitRoom(true, call.id);

      if (!acquireCallLock(call.id)) {
        connectingCallIdRef.current = null;
        return;
      }
      setCallConnectionState("connecting");

      const room = new Room();

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind !== "audio") return;

        const element = track.attach() as HTMLMediaElement;
        const trackSid = track.sid;
        if (!trackSid) return;

        element.autoplay = true;
        element.setAttribute("playsinline", "true");
        element.style.display = "none";
        document.body.appendChild(element);
        remoteAudioElementsRef.current.set(trackSid, element);
        void element.play?.().catch(() => undefined);
      });

      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        const trackSid = track.sid;
        if (!trackSid) return;

        const attached = remoteAudioElementsRef.current.get(trackSid);
        if (attached) {
          try {
            track.detach(attached);
          } catch {
            // noop
          }
          attached.remove();
          remoteAudioElementsRef.current.delete(trackSid);
        }
      });

      room.on(RoomEvent.ParticipantConnected, () => {
        clearRemoteAbsentTimer();
      });

      room.on(RoomEvent.ParticipantDisconnected, () => {
        clearRemoteAbsentTimer();

        remoteAbsentTimerRef.current = setTimeout(() => {
          const currentCall = activeCallRef.current;
          if (
            currentCall &&
            currentCall.id === call.id &&
            isConnectedCallStatus(currentCall)
          ) {
            void endCurrentCallRef.current("remote_participant_disconnected");
          }
        }, REMOTE_ABSENCE_GRACE_MS);
      });

      room.on(RoomEvent.Reconnecting, () => {
        setCallConnectionState("reconnecting");
      });

      room.on(RoomEvent.Reconnected, () => {
        setCallConnectionState("connected");
      });

      room.on(RoomEvent.Disconnected, () => {
        cleanupRemoteAudioElements();
        connectedCallIdRef.current = null;
        connectingCallIdRef.current = null;
        setCallConnectionState("idle");
      });

      try {
        await room.connect(call.livekit.url, call.livekit.token, {
          autoSubscribe: true,
        });

        const currentLock = readCallLock();
        const stillOwnsCall =
          currentLock?.callId === call.id &&
          currentLock.ownerTabId === tabIdRef.current;
        if (
          !stillOwnsCall ||
          connectingCallIdRef.current !== call.id
        ) {
          room.disconnect();
          if (connectingCallIdRef.current === call.id) {
            connectingCallIdRef.current = null;
          }
          return;
        }

        liveKitRoomRef.current = room;
        await room.localParticipant.setMicrophoneEnabled(true);
        setIsCallMuted(false);
        setCallConnectionState("connected");
        connectedCallIdRef.current = call.id;
        connectingCallIdRef.current = null;

        const response = await ChatCallService.connected(call.id);
        if (response?.data?.success && response.data.data) {
          const connectedCall = response.data.data;
          activeCallRef.current = connectedCall;
          setActiveCall(connectedCall);
        }
      } catch (error: unknown) {
        await cleanupLiveKitRoom();
        const message = resolveCallErrorMessage(
          error,
          "Falha ao conectar ao áudio da chamada.",
        );
        const code = /token/i.test(String(message))
          ? "token_expired"
          : "connection_failed";

        await reportCallFailure(call, code, message, {
          source: "livekit_connect",
        });
      }
    },
    [
      acquireCallLock,
      cleanupLiveKitRoom,
      cleanupRemoteAudioElements,
      clearRemoteAbsentTimer,
      enabled,
      reportCallFailure,
      stopRingtone,
      userId,
    ],
  );

  const endCurrentCall = useCallback(
    async (reason = "completed") => {
      const currentCall = activeCallRef.current;
      if (!currentCall) return;

      setIsCallActionLoading(true);
      try {
        const response = await ChatCallService.end(currentCall.id, { reason });
        if (response?.data?.success) {
          await applyCallState(response.data.data);
          return;
        }

        throw new Error(
          resolveCallErrorMessage(response, "Não foi possível encerrar a chamada."),
        );
      } catch (error: unknown) {
        toast.error(
          resolveCallErrorMessage(error, "Não foi possível encerrar a chamada."),
        );
      } finally {
        setIsCallActionLoading(false);
      }
    },
    [applyCallState],
  );

  const cancelOutgoingCall = useCallback(async () => {
    const currentCall = activeCallRef.current;
    if (!currentCall) return;

    setIsCallActionLoading(true);
    try {
      const response = await ChatCallService.cancel(currentCall.id, {
        reason: "cancelled_by_caller",
      });

      if (response?.data?.success) {
        await applyCallState(response.data.data);
        return;
      }

      throw new Error(
        resolveCallErrorMessage(response, "Não foi possível cancelar a chamada."),
      );
    } catch (error: unknown) {
      toast.error(
        resolveCallErrorMessage(error, "Não foi possível cancelar a chamada."),
      );
    } finally {
      setIsCallActionLoading(false);
    }
  }, [applyCallState]);

  const rejectIncomingCall = useCallback(async () => {
    const currentCall = activeCallRef.current;
    if (!currentCall) return;

    setIsCallActionLoading(true);
    try {
      const response = await ChatCallService.reject(currentCall.id, {
        reason: "declined",
      });

      if (response?.data?.success) {
        await applyCallState(response.data.data);
        return;
      }

      throw new Error(
        resolveCallErrorMessage(response, "Não foi possível recusar a chamada."),
      );
    } catch (error: unknown) {
      toast.error(
        resolveCallErrorMessage(error, "Não foi possível recusar a chamada."),
      );
    } finally {
      setIsCallActionLoading(false);
    }
  }, [applyCallState]);

  const answerIncomingCall = useCallback(async () => {
    const currentCall = activeCallRef.current;
    if (!currentCall) return;

    setIsCallActionLoading(true);
    try {
      await requestMicrophonePermission();

      const response = await ChatCallService.accept(currentCall.id);
      if (response?.data?.success && response.data.data) {
        const nextCall = response.data.data;
        await applyCallState(nextCall);
        if (nextCall.livekit) {
          await connectToLiveKit(nextCall);
        }
        return;
      }

      throw new Error(
        resolveCallErrorMessage(response, "Não foi possível atender a chamada."),
      );
    } catch (error: unknown) {
      const message = resolveCallErrorMessage(
        error,
        "Não foi possível acessar o microfone.",
      );

      if (isMicrophonePermissionError(error)) {
        try {
          const response = await ChatCallService.reject(currentCall.id, {
            reason: "microphone_denied",
          });

          if (response?.data?.success) {
            await applyCallState(response.data.data);
          }
        } catch {
          // noop
        }
      }

      toast.error(message);
    } finally {
      setIsCallActionLoading(false);
    }
  }, [applyCallState, connectToLiveKit, requestMicrophonePermission]);

  const toggleCallMicrophone = useCallback(async () => {
    const room = liveKitRoomRef.current;
    if (!room) return;

    setIsCallActionLoading(true);
    try {
      const nextEnabled = isCallMuted;
      await room.localParticipant.setMicrophoneEnabled(nextEnabled);
      setIsCallMuted(!nextEnabled);
    } catch (error: unknown) {
      toast.error(
        resolveCallErrorMessage(
          error,
          "Não foi possível alterar o estado do microfone.",
        ),
      );
    } finally {
      setIsCallActionLoading(false);
    }
  }, [isCallMuted]);

  const cancelVoiceCallConfirmation = useCallback(() => {
    if (isCallActionLoading) return;
    setPendingCallRequest(null);
    setCallConfirmationResult(null);
  }, [isCallActionLoading]);

  const retryVoiceCallConfirmation = useCallback(() => {
    if (isCallActionLoading || !pendingCallRequest) return;
    setCallConfirmationResult(null);
  }, [isCallActionLoading, pendingCallRequest]);

  const confirmVoiceCall = useCallback(async () => {
    if (!pendingCallRequest || confirmCallInFlightRef.current) return;

    let preservePendingRequest = false;
    confirmCallInFlightRef.current = true;
    setIsCallActionLoading(true);
    try {
      const canStartCall = await ensureRoomIsReadyForNewCall(
        pendingCallRequest.roomId,
      );
      if (!canStartCall) {
        return;
      }

      await requestMicrophonePermission();

      const response = await ChatCallService.create({
        roomId: pendingCallRequest.roomId,
      });

      if (response?.data?.success && response.data.data) {
        const nextCall = response.data.data;
        preservePendingRequest =
          (nextCall.rawStatus || nextCall.status) ===
          "recipient_unavailable";
        await applyCallState(nextCall);

        if (nextCall.rawStatus === "calling") {
          toast.info("Ligando...");
        }

        return;
      }

      if (
        response?.data?.code === "recipient_unavailable" ||
        (response?.data?.data?.rawStatus || response?.data?.data?.status) ===
          "recipient_unavailable"
      ) {
        preservePendingRequest = true;
        showRecipientUnavailable(pendingCallRequest);
        return;
      }

      const existingCallId =
        typeof response?.data?.details?.callId === "string"
          ? response.data.details.callId
          : null;

      if (existingCallId) {
        const existingResponse = await ChatCallService.getCurrent(existingCallId);

        if (existingResponse?.data?.success && existingResponse.data.data) {
          await applyCallState(existingResponse.data.data);
          return;
        }
      }

      throw new Error(
        resolveCallErrorMessage(response, "Não foi possível iniciar a chamada."),
      );
    } catch (error: unknown) {
      const typedError = error as ErrorWithMessage | null;
      if (typedError?.response?.data?.code === "recipient_unavailable") {
        preservePendingRequest = true;
        showRecipientUnavailable(pendingCallRequest);
      } else if (isMicrophonePermissionError(error)) {
        toast.error(
          "O acesso ao microfone foi negado. Libere a permissão para iniciar a chamada.",
        );
      } else {
        toast.error(
          resolveCallErrorMessage(error, "Não foi possível iniciar a chamada."),
        );
      }
    } finally {
      if (!preservePendingRequest) {
        setPendingCallRequest(null);
      }
      confirmCallInFlightRef.current = false;
      setIsCallActionLoading(false);
    }
  }, [
    applyCallState,
    ensureRoomIsReadyForNewCall,
    pendingCallRequest,
    requestMicrophonePermission,
    showRecipientUnavailable,
  ]);

  const requestVoiceCall = useCallback(
    async (room: RoomCandidate) => {
      if (!enabled || !userId) return;

      const { error, roomId, participant } = buildParticipant(room, userId);

      if (activeCallRef.current && isLiveCallStatus(activeCallRef.current)) {
        if (String(activeCallRef.current.roomId) !== roomId) {
          toast.warning("Já existe uma chamada ativa ou pendente em andamento.");
          return;
        }
      }

      if (error || !participant) {
        toast.error(error || "Não foi possível iniciar a chamada.");
        return;
      }

      setPendingCallRequest({
        roomId,
        participant,
      });
      setCallConfirmationResult(null);
    },
    [enabled, userId],
  );

  const closeCallForRoom = useCallback(
    async (roomId: string | number, reason = "chat_window_closed") => {
      const normalizedRoomId = String(roomId);

      if (pendingCallRequest?.roomId === normalizedRoomId) {
        setPendingCallRequest(null);
        setCallConfirmationResult(null);
      }

      const currentCall = activeCallRef.current;
      if (!currentCall || !isLiveCallStatus(currentCall)) {
        return;
      }

      if (String(currentCall.roomId) !== normalizedRoomId) {
        return;
      }

      try {
        await terminateCall(currentCall, reason);
      } catch (error: unknown) {
        toast.error(
          resolveCallErrorMessage(error, "Não foi possível finalizar a chamada ao fechar o chat."),
        );
      }
    },
    [pendingCallRequest, terminateCall],
  );

  useEffect(() => {
    endCurrentCallRef.current = endCurrentCall;
  }, [endCurrentCall]);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    if (!enabled || !userId) {
      void finalizeCallState(null);
      setPendingCallRequest(null);
      setCallConfirmationResult(null);
      return;
    }

    void syncCurrentCallFromServer();
  }, [enabled, finalizeCallState, syncCurrentCallFromServer, userId]);

  useEffect(() => {
    if (!activeCall || !enabled || !userId) {
      stopHeartbeat();
      stopRingtone();
      setCallDurationSeconds(0);
      return;
    }

    if (!isLiveCallStatus(activeCall)) {
      return;
    }

    const ownsCall = acquireCallLock(activeCall.id);
    if (!ownsCall) {
      stopHeartbeat();
      stopRingtone();
      void cleanupLiveKitRoom();
      return;
    }

    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      acquireCallLock(activeCall.id);
    }, 5000);

    if (activeCall.status === "incoming") {
      void startRingtone();
    } else {
      stopRingtone();
    }

    if (activeCall.livekit && isConnectedCallStatus(activeCall)) {
      void connectToLiveKit(activeCall);
    }

    return () => {
      stopHeartbeat();
    };
  }, [
    acquireCallLock,
    activeCall,
    cleanupLiveKitRoom,
    connectToLiveKit,
    enabled,
    startRingtone,
    stopHeartbeat,
    stopRingtone,
    userId,
  ]);

  useEffect(() => {
    if (!activeCall || !isConnectedCallStatus(activeCall) || !activeCall.answeredAt) {
      setCallDurationSeconds(0);
      return;
    }

    const updateDuration = () => {
      const startedAt = new Date(activeCall.answeredAt || "").getTime();
      if (!startedAt) {
        setCallDurationSeconds(activeCall.durationSeconds || 0);
        return;
      }

      setCallDurationSeconds(
        Math.max(0, Math.floor((Date.now() - startedAt) / 1000)),
      );
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [activeCall]);

  useEffect(() => {
    if (!enabled || !userId) return;

    const socket = getSocket();
    const eventName = `notification:chat:call:${userId}`;
    const handler = async (payload: { call?: ChatCall | null }) => {
      if (!payload?.call) return;
      await applyCallState(payload.call);
    };
    const handleSocketReady = () => {
      void syncCurrentCallFromServer();
    };

    const setupListeners = () => {
      socket.off(eventName, handler);
      socket.off("socket:ready", handleSocketReady);
      socket.on(eventName, handler);
      socket.on("socket:ready", handleSocketReady);
    };

    if (socket.connected) {
      setupListeners();
      void syncCurrentCallFromServer();
    } else {
      const onConnect = () => {
        setupListeners();
        void syncCurrentCallFromServer();
      };

      socket.on("connect", onConnect);
      socket.connect();

      return () => {
        socket.off("connect", onConnect);
        socket.off(eventName, handler);
        socket.off("socket:ready", handleSocketReady);
      };
    }

    return () => {
      socket.off(eventName, handler);
      socket.off("socket:ready", handleSocketReady);
    };
  }, [applyCallState, enabled, syncCurrentCallFromServer, userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    if (!activeCall || !isLiveCallStatus(activeCall)) return;

    const interval = setInterval(() => {
      void syncCurrentCallFromServer();
    }, 4000);

    return () => clearInterval(interval);
  }, [activeCall, enabled, syncCurrentCallFromServer, userId]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CALL_LOCK_KEY) return;

      const currentCall = activeCallRef.current;
      if (!currentCall || !isLiveCallStatus(currentCall)) return;

      const currentLock = readCallLock();
      if (!currentLock || currentLock.callId !== currentCall.id) {
        const ownsCall = acquireCallLock(currentCall.id);
        if (ownsCall && currentCall.livekit && isConnectedCallStatus(currentCall)) {
          void connectToLiveKit(currentCall);
        }
        return;
      }

      const isOwner = currentLock.ownerTabId === tabIdRef.current;
      setIsCallControlOwner(isOwner);

      if (!isOwner) {
        stopRingtone();
        void cleanupLiveKitRoom();
      }
    };

    const handleWindowFocus = () => {
      void syncCurrentCallFromServer();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("online", handleWindowFocus);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("online", handleWindowFocus);
    };
  }, [
    acquireCallLock,
    cleanupLiveKitRoom,
    connectToLiveKit,
    stopRingtone,
    syncCurrentCallFromServer,
  ]);

  useEffect(() => {
    if (!enabled || !userId) return;

    const handlePageHide = () => {
      const currentCall = activeCallRef.current;
      if (!currentCall || !isLiveCallStatus(currentCall)) {
        return;
      }

      const currentLock = readCallLock();
      const ownsCall =
        currentLock?.callId === currentCall.id &&
        currentLock.ownerTabId === tabIdRef.current;
      if (
        !ownsCall ||
        unloadTerminatedCallIdRef.current === currentCall.id
      ) {
        return;
      }

      unloadTerminatedCallIdRef.current = currentCall.id;

      stopRingtone();
      sendKeepaliveTermination(currentCall, "page_unload");
      releaseCallLock(currentCall.id);
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
  }, [enabled, releaseCallLock, sendKeepaliveTermination, stopRingtone, userId]);

  useEffect(() => {
    return () => {
      stopHeartbeat();
      stopRingtone();
      releaseCallLock(activeCallRef.current?.id);
      clearRemoteAbsentTimer();
      void cleanupLiveKitRoom();
    };
  }, [
    cleanupLiveKitRoom,
    clearRemoteAbsentTimer,
    releaseCallLock,
    stopHeartbeat,
    stopRingtone,
  ]);

  const isIncomingCallOpen = useMemo(
    () =>
      Boolean(
        activeCall &&
          activeCall.status === "incoming" &&
          isCallControlOwner &&
          enabled,
      ),
    [activeCall, enabled, isCallControlOwner],
  );

  const isOngoingCallOpen = useMemo(
    () =>
      Boolean(
        activeCall &&
          ["calling", "answered", "in_progress"].includes(
            activeCall.rawStatus || "",
          ) &&
          activeCall.status !== "incoming" &&
          isCallControlOwner &&
          enabled,
      ),
    [activeCall, enabled, isCallControlOwner],
  );

  return {
    activeCall,
    isCallActionLoading,
    isCallMuted,
    isIncomingCallOpen,
    isOngoingCallOpen,
    isCallControlOwner,
    ringtoneBlocked,
    callDurationSeconds,
    callConnectionState,
    isCallConfirmationOpen: Boolean(
      pendingCallRequest || callConfirmationResult,
    ),
    callConfirmationParticipant: pendingCallRequest?.participant || null,
    callConfirmationResult,
    requestVoiceCall,
    confirmVoiceCall,
    cancelVoiceCallConfirmation,
    retryVoiceCallConfirmation,
    closeCallForRoom,
    answerIncomingCall,
    rejectIncomingCall,
    cancelOutgoingCall,
    endCurrentCall: async () => endCurrentCall("completed"),
    toggleCallMicrophone,
  };
}
